import { useState } from "react";
import { Link } from "react-router";
import { useListContext } from "ra-core";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Payment } from "../../types";

const WEEKDAYS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const MONTHS = [
  "Январь","Февраль","Март","Апрель","Май","Июнь",
  "Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь",
];

const styleFor = (p: Payment): string => {
  const isExpense = p.direction === "expense";
  if (p.status === "overdue") {
    return "bg-amber-100 text-amber-900 border-amber-400";
  }
  if (p.status === "paid") {
    return isExpense
      ? "bg-rose-100 text-rose-800 border-rose-300"
      : "bg-emerald-100 text-emerald-800 border-emerald-300";
  }
  // pending
  return isExpense
    ? "bg-rose-50 text-rose-700 border-rose-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
};

export const PaymentCalendar = () => {
  const { data = [] } = useListContext<Payment>();
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const byDate: Record<string, Payment[]> = {};
  for (const p of data) {
    const key = p.planned_date?.slice(0, 10);
    if (key) (byDate[key] ??= []).push(p);
  }

  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = today.toISOString().slice(0, 10);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="icon" onClick={prevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="font-semibold text-base">
          {MONTHS[month]} {year}
        </span>
        <Button variant="outline" size="icon" onClick={nextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-7 border-l border-t rounded-md overflow-hidden">
        {WEEKDAYS.map(d => (
          <div key={d} className="border-r border-b bg-muted px-2 py-1 text-center text-xs font-medium text-muted-foreground">
            {d}
          </div>
        ))}

        {cells.map((day, i) => {
          if (!day) return (
            <div key={`empty-${i}`} className="border-r border-b min-h-20 bg-muted/30" />
          );

          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const payments = byDate[dateStr] ?? [];
          const isToday = dateStr === todayStr;

          return (
            <div key={dateStr} className={cn("border-r border-b min-h-20 p-1.5 flex flex-col gap-1", isToday && "bg-accent/40")}>
              <span className={cn(
                "text-xs font-medium self-start w-6 h-6 flex items-center justify-center rounded-full",
                isToday ? "bg-primary text-primary-foreground" : "text-muted-foreground"
              )}>
                {day}
              </span>
              {payments.map(p => {
                const sign = p.direction === "expense" ? "−" : "+";
                const label = p.vendor_name?.trim();
                return (
                  <Link
                    key={p.id}
                    to={`/payments/${p.id}/show`}
                    className={cn(
                      "text-xs px-1.5 py-0.5 rounded border truncate block hover:opacity-80",
                      styleFor(p),
                    )}
                    title={`${label ? label + " · " : ""}${sign}${p.amount?.toLocaleString("ru")} ₽`}
                  >
                    {sign}{p.amount?.toLocaleString("ru")} ₽
                  </Link>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block"/>Доход</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-100 border border-rose-300 inline-block"/>Расход</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-400 inline-block"/>Просрочено</span>
      </div>
    </div>
  );
};
